import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  Flex,
  HStack,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaCaretDownSolid } from '@enechain/ecloud-icons'

import { HintTooltip } from '~/components/common/HintTooltip'
import { iconSize } from '~/constants/iconSize'
import { useDropdownDismiss } from '~/hooks/useDropdownDismiss'

/**
 * ▼ は DS `Select` と同じ `FaCaretDownSolid` 20px / `Object.midEmphasis`。 開いても回さない
 * (UI 辞書 components/select.md・multi-select-summary.md「枠・caret・開いた時のフォーカスリングは
 * DS `Select` と同じ」。 chevron は使わない)。
 */
const CaretDownIcon = (): React.ReactElement => (
  <Box as="span" display="inline-flex" color="Object.midEmphasis">
    <FaCaretDownSolid boxSize={iconSize.labeledButton} aria-hidden="true" />
  </Box>
)

/** select.md「開いた時のリスト」: control の真下 8px。 menu の最小幅は design の 260px。 */
const MENU_GAP = 8
const MENU_MIN_WIDTH = 260
/** viewport の端との余白 (`MultiSelect` と同じ)。 */
const MENU_EDGE = 8

/**
 * menu の viewport 座標。 左揃えを既定にし、 右端が viewport を超える時だけ control の
 * 右端に揃える (`right` で固定する)。
 */
type MenuRect = { top: number; left?: number; right?: number }

export type FilterMultiSelectOption = {
  value: string
  label: string
  /** option label の左側に表示する small dot 色 (= design `ecl-multi-select__chip-dot`)。 */
  dotColor?: string
}

type Props = {
  options: FilterMultiSelectOption[]
  /** 現在選択中の value Set。 空 Set = 全件 (= 「すべて」 placeholder 表示)。 */
  selected: Set<string>
  /** 新しい Set 全体を渡す形 (= 親が state を完全に管理)。 */
  onChange: (next: Set<string>) => void
  /** trigger button の最小幅 (px)。 design 仕様: `min-width: 180px`。 */
  minWidth?: number | string
  /** 全選択 placeholder の文言。 default は common:filter.all。 */
  allLabel?: string
  /**
   * 要約表示 (UI 辞書 components/multi-select-summary.md)。 trigger に選択肢の順で先頭のラベル +
   * `+N` を出して全件を tooltip に出し、 メニュー先頭に「すべて」 (押すと全解除) を置く。
   * 見出し・ツールバーの中で、 選んだ数で幅も高さも変えたくない所に使う。 既定は「N件選択」。
   */
  summary?: boolean
  /** trigger の固定幅。 指定時は `minWidth` より優先し、 中身で伸ばさない (要約表示の置き場ごとの幅)。 */
  width?: number | string
  /** trigger の名前 (可視ラベルが隣にあっても、 trigger の文言は選択値なので名前を別に渡す)。 */
  'aria-label'?: string
}

/**
 * filter 用途の checkbox 型 multi-select 共通部品 (人選択 typeahead 型は MultiSelect)。
 *
 * design HTML `design/bidding/spot.html:120-216` `.ecl-multi-select` 準拠。 枠・caret・開いた時の
 * フォーカスリング・リストは DS `Select` にそろえる (UI 辞書 components/select.md「複数選択」、
 * multi-select-summary.md、 2026-10-14 ebalance-prototype#592):
 *   - trigger control: 40px、 180px min-width、 1px `Border.midEmphasis`、 ▼ (caret) 右付き。
 *     開いている間は枠の色を変えず 3px の `Focus.clarity` リングだけ。 ▼ は回さない
 *   - 0 件の「すべて」 は未入力の案内ではなく実在の状態なので `Text.midEmphasis` (placeholder 色にしない)
 *   - menu: 260px width、 surface-primary bg、 上下 8px、 影 shadSm、 radius md
 *   - option: 8px 12px padding、 hover `Hover.accentPrimaryLow`、 選択中 `Surface.accentPrimaryLightLow`、
 *     checkbox + (optional dot) + label
 *
 * Chakra v3 (3.29.0) の `Menu` namespace primitive (`MenuTrigger` 等) の型定義は react-19
 * 前提 (ecloud-designsystem 0.14.2 の peerDependency は react ^18.2.0) のため、本 app
 * (react-18) の JSX 型と噛み合わず利用できない。 そのため開閉 state を自前管理する
 * `Box` / `Flex` ベースの軽量 dropdown として実装する
 * (= outside-click / Escape での dismiss も自前で持つ)。
 */
export const FilterMultiSelect = ({
  options,
  selected,
  onChange,
  minWidth = '180px',
  allLabel,
  summary = false,
  width,
  'aria-label': ariaLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [menuRect, setMenuRect] = useState<MenuRect | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  // Menu primitive の代替として outside-click / Escape での閉じる挙動を自前で持つ。
  useDropdownDismiss(rootRef, isOpen, () => setIsOpen(false))

  // menu は position:fixed で、 祖先の `overflow: hidden` (板パネルの見出し等) で切れないようにする。
  // DOM は trigger の直後に置いたまま (body へ portal しない) にして、 trigger から Tab で選択肢へ
  // 進めるようにする (design `ecl-multi-select.js` も menu を control の直後に置いて fixed で出す)。
  // 表示のたびに control の viewport 座標を測って追従させる。
  const updateMenuRect = useCallback((): void => {
    const rect = rootRef.current?.getBoundingClientRect()
    if (rect === undefined) return
    // fixed の `right` の基準は縦スクロールバーを除いた幅なので、 innerWidth ではなく clientWidth で測る
    const viewportWidth = document.documentElement.clientWidth
    const overflowsRight =
      rect.left + MENU_MIN_WIDTH > viewportWidth - MENU_EDGE
    const next: MenuRect = overflowsRight
      ? { top: rect.bottom + MENU_GAP, right: viewportWidth - rect.right }
      : { top: rect.bottom + MENU_GAP, left: rect.left }
    setMenuRect((prev) =>
      prev !== null &&
      prev.top === next.top &&
      prev.left === next.left &&
      prev.right === next.right
        ? prev
        : next,
    )
  }, [])

  // 測定は paint 前に行う。 scroll は capture 相で祖先のスクロールコンテナの分も拾う。
  useLayoutEffect(() => {
    if (!isOpen) return
    updateMenuRect()
    window.addEventListener('scroll', updateMenuRect, true)
    window.addEventListener('resize', updateMenuRect)
    return () => {
      window.removeEventListener('scroll', updateMenuRect, true)
      window.removeEventListener('resize', updateMenuRect)
    }
  }, [isOpen, updateMenuRect])

  const toggle = (value: string): void => {
    const next = new Set(selected)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    onChange(next)
  }

  const resolvedAllLabel = allLabel ?? t('common:filter.all')
  const triggerLabel =
    selected.size === 0
      ? resolvedAllLabel
      : t('common:filter.selectedCount', { count: selected.size })
  const isPlaceholder = selected.size === 0
  // 要約表示の先頭は選んだ順ではなく選択肢の順 (multi-select-summary.md)
  const picked = options.filter((opt) => selected.has(opt.value))

  return (
    <Box ref={rootRef} position="relative" display="inline-block">
      {/* 中身 (24px の行) を 40px の枠の縦中央に置き、 隣の DS Select / MultiSelect と文字の高さを揃える */}
      <Box
        alignItems="center"
        display="flex"
        width={width}
        minWidth={width === undefined ? minWidth : undefined}
        height="40px"
        paddingRight={2}
        paddingLeft={3}
        fontSize="md"
        textAlign="left"
        borderWidth="1px"
        borderColor="Border.midEmphasis"
        borderRadius="md"
        boxShadow={isOpen ? '0 0 0 3px {colors.Focus.clarity}' : undefined}
        _focusVisible={{
          outline: 'none',
          boxShadow: '0 0 0 3px {colors.Focus.clarity}',
        }}
        cursor="pointer"
        transition="border-color 0.12s, box-shadow 0.12s"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        backgroundColor="Surface.primary"
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsOpen((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          flex={1}
          gap={2}
          minWidth={0}
          minHeight="24px"
        >
          {summary && picked.length > 0 ? (
            // 先頭のラベル + `+N`。 収まらない時は先頭のラベルだけを省略し `+N` は削らない。
            // 全件は tooltip (multi-select-summary.md「閉じた状態」)
            <HintTooltip
              content={picked
                .map((opt) => opt.label)
                .join(t('common:filter.listSeparator'))}
            >
              <HStack gap={1} minWidth={0} fontSize="md">
                <Text
                  overflow="hidden"
                  color="Text.highEmphasis"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                >
                  {picked[0].label}
                </Text>
                {picked.length > 1 && (
                  <Text
                    flexShrink={0}
                    color="Text.midEmphasis"
                    fontVariantNumeric="tabular-nums"
                  >
                    +{picked.length - 1}
                  </Text>
                )}
              </HStack>
            </HintTooltip>
          ) : (
            <Text
              color={isPlaceholder ? 'Text.midEmphasis' : 'Text.highEmphasis'}
              fontSize="md"
              whiteSpace="nowrap"
            >
              {triggerLabel}
            </Text>
          )}
          <CaretDownIcon />
        </Flex>
      </Box>
      {/* select.md「開いた時のリスト」: control の真下 8px / 上下 8px / 影 shadSm。
          trigger が `aria-haspopup="listbox"` を宣言しているので、 開いた先も listbox として名乗る
          (複数選択なので `aria-multiselectable`)。 座標は連続量なので inline style で渡す
          (Chakra の props だと値ごとに class が増える)。 */}
      {isOpen && menuRect !== null && (
        <Box
          position="fixed"
          zIndex="popover"
          minWidth={`${MENU_MIN_WIDTH}px`}
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="md"
          boxShadow="shadSm"
          aria-multiselectable
          backgroundColor="Surface.primary"
          paddingX={0}
          paddingY={2}
          role="listbox"
          style={{
            top: `${menuRect.top}px`,
            ...(menuRect.right !== undefined
              ? { right: `${menuRect.right}px` }
              : { left: `${menuRect.left ?? 0}px` }),
          }}
        >
          {summary && (
            // multi-select-summary.md「開いた状態」: 先頭の「すべて」 は選択 0 件の時だけチェック。
            // 押すと全解除 (外せない)。 下に区切り線
            <Box
              marginBottom={1}
              borderColor="Border.lowEmphasis"
              borderBottomWidth="1px"
            >
              <OptionRow
                isSelected={selected.size === 0}
                label={resolvedAllLabel}
                onSelect={() => onChange(new Set())}
              />
            </Box>
          )}
          {options.map((opt) => (
            <OptionRow
              key={opt.value}
              dotColor={opt.dotColor}
              isSelected={selected.has(opt.value)}
              label={opt.label}
              onSelect={() => toggle(opt.value)}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

/** メニューの 1 行 (checkbox + 任意の dot + ラベル)。 */
const OptionRow = ({
  label,
  isSelected,
  onSelect,
  dotColor,
}: {
  label: string
  isSelected: boolean
  onSelect: () => void
  dotColor?: string
}): React.ReactElement => (
  <Flex
    alignItems="center"
    gap={2}
    fontSize="md"
    _hover={{ backgroundColor: 'Hover.accentPrimaryLow' }}
    cursor="pointer"
    // 選択肢はキーボードでも操作できる必要がある。 中の DS `Checkbox` は
    // `onChange` を持たない見た目専用で、 focus は当たっても Space では
    // 選べない。 trigger と同じく行そのものを操作対象にする。
    aria-selected={isSelected}
    backgroundColor={isSelected ? 'Surface.accentPrimaryLightLow' : undefined}
    onClick={onSelect}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onSelect()
      }
    }}
    paddingX={3}
    paddingY={2}
    role="option"
    tabIndex={0}
  >
    {/* 見た目専用。 選択状態は行の `aria-selected` が伝えるので支援技術
        からは隠し、 Tab 順にも入れない。 */}
    <Checkbox
      pointerEvents="none"
      aria-hidden
      checked={isSelected}
      inputProps={{ tabIndex: -1 }}
    />
    {dotColor !== undefined && (
      <Box
        flexShrink={0}
        width="8px"
        height="8px"
        borderRadius="50%"
        backgroundColor={dotColor}
      />
    )}
    <Text
      flex={1}
      overflow="hidden"
      color="Text.highEmphasis"
      whiteSpace="nowrap"
      textOverflow="ellipsis"
    >
      {label}
    </Text>
  </Flex>
)
